/*
 * The source-view surface (Q21, spec 12 §4.4, spec 08 §4, F-2 §3.2, §6.2): a plain auto-growing
 * textarea over the shared composer core and frame. Its text lives in the draft layer from the
 * first keystroke, so nothing typed is ever only in React state. Enter sends and Shift+Enter
 * inserts a newline — invertible per user, and never a send inside a fenced block or while an IME
 * is composing; the markdown shortcuts, list continuation and the one autocomplete combobox edit
 * the box through `applyEdit`, so every one of them is a single step in the browser's own undo
 * stack. What is typed here is markdown, which is why this surface is the one that offers the
 * preview — and why its footer menu is the way back to the formatted editor.
 *
 * It is also what a person sees while the editor's chunk is still on its way: `loading` renders
 * the same textarea without the preview toggle and the source switch, over the very core the editor
 * then takes over, so the swap loses nothing that was typed into it — and it writes down whether it
 * had the caret (`standIn`), which is the one thing the swap cannot carry over by construction.
 */

import {
	useCallback,
	useEffect,
	useImperativeHandle,
	useLayoutEffect,
	useRef,
	useState,
} from "react"
import { Button } from "../../ui/index.ts"
import { currentOutbox } from "../drafts/outbox.ts"
import { t, translate } from "../i18n/index.ts"
import { Icon } from "../shell/index.ts"
import { namePastedImage, pastedContent } from "./attachments/staging.ts"
import { AutocompleteList } from "./autocomplete/AutocompleteList.tsx"
import { useAutocomplete } from "./autocomplete/useAutocomplete.ts"
import "./composer.css"
import { ComposerFrame } from "./ComposerFrame.tsx"
import { noHoverPointer } from "./pointer.ts"
import { PreviewPane } from "./PreviewPane.tsx"
import { setSourceMode } from "./settings.ts"
import { applyEdit, type Edit } from "./text/edit.ts"
import { caretInFencedBlock } from "./text/fence.ts"
import { continueList, indentList } from "./text/lists.ts"
import { pasteEdit, pasteToMarkdown } from "./text/paste.ts"
import { applyShortcut, shortcutFor, type Placeholders } from "./text/shortcuts.ts"
import type { ComposerSurfaceProps } from "./useComposerCore.ts"

/** The IME guard of spec 12 §4.4: a composing Enter commits a candidate and must never send. */
function isComposing(e: React.KeyboardEvent): boolean {
	return e.nativeEvent.isComposing || e.keyCode === 229
}

export interface TextareaComposerProps extends ComposerSurfaceProps {
	/** True while this textarea stands in for the formatted editor whose chunk is still loading. */
	loading?: boolean
}

export function TextareaComposer(props: TextareaComposerProps) {
	const { core, targetKey, placeholderKey, placeholderArgs, sources, preview, renderPreview, ref } =
		props
	const noteCaret = props.loading === true ? props.noteCaret : undefined
	const textareaRef = useRef<HTMLTextAreaElement>(null)
	const { attachments, draft, settings, value, setValue, disabled } = core
	const [caret, setCaret] = useState<number | null>(null)
	const [previewOpen, setPreviewOpen] = useState(settings.previewOpen)

	const apply = useCallback(
		(edit: Edit) => {
			const ta = textareaRef.current
			if (ta === null) return
			applyEdit(ta, edit)
			setValue(ta.value)
			setCaret(ta.selectionStart)
		},
		[setValue],
	)

	// The combobox names the range it wants replaced; a textarea's edit is that range spliced into
	// the value, with the caret behind the insertion.
	const onReplace = useCallback(
		(range: { start: number; end: number }, insert: string) => {
			const at = range.start + insert.length
			apply({
				text: value.slice(0, range.start) + insert + value.slice(range.end),
				start: at,
				end: at,
			})
		},
		[apply, value],
	)

	const autocomplete = useAutocomplete({ sources, value, caret, onReplace })

	useImperativeHandle(
		ref,
		() => ({
			focus: () => textareaRef.current?.focus(),
			getValue: () => textareaRef.current?.value ?? value,
			setValue: (v, where = "end") => {
				const ta = textareaRef.current
				const keep =
					where === "keep" ? Math.min(ta?.selectionStart ?? v.length, v.length) : v.length
				setValue(v)
				if (ta !== null) {
					ta.value = v
					ta.setSelectionRange(keep, keep)
				}
			},
			insertAtCaret: (text) => {
				const ta = textareaRef.current
				const at = ta?.selectionStart ?? value.length
				const to = ta?.selectionEnd ?? at
				apply({
					text: value.slice(0, at) + text + value.slice(to),
					start: at + text.length,
					end: at + text.length,
				})
			},
			caretInFencedBlock: () =>
				caretInFencedBlock(value, textareaRef.current?.selectionStart ?? value.length),
		}),
		[apply, setValue, value],
	)

	// A box a surface opened for one purpose — an in-place edit, a thread reply — takes the caret
	// with it. The `autoFocus` attribute would do the same, and would also take it back on every
	// re-render of the surface that owns the box.
	const { autoFocus } = props
	useEffect(() => {
		if (autoFocus === true) textareaRef.current?.focus()
	}, [autoFocus])

	// Auto-grow: one row at least, then the content's height, capped by the stylesheet.
	useLayoutEffect(() => {
		const ta = textareaRef.current
		if (ta === null) return
		ta.style.height = "auto"
		ta.style.height = `${ta.scrollHeight}px`
	}, [value])

	const placeholders: Placeholders = {
		bold: t("composer.placeholder.bold"),
		italic: t("composer.placeholder.italic"),
		strike: t("composer.placeholder.strike"),
		code: t("composer.placeholder.code"),
		linkText: t("composer.placeholder.linkText"),
		url: t("composer.placeholder.url"),
	}

	const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
		const ta = e.currentTarget
		const start = ta.selectionStart
		const end = ta.selectionEnd
		if (autocomplete.onKeyDown(e)) return

		if (e.key === "Enter") {
			if (isComposing(e)) return
			const inFence = caretInFencedBlock(value, start)
			// Ctrl/Cmd+Enter sends from anywhere, the keyboard's way out of a fence (F-2 D7). Plain
			// Enter sends only where a pointer can hover: on a phone it starts a new line and the
			// send button is what posts (D-10 D14).
			const chord = e.ctrlKey || e.metaKey
			// A list is the one structure where a second line is the point, so Enter belongs to the list
			// there whatever the setting says (X-B1 D2, the rule the formatted editor follows in
			// `core/editor/keymap.ts`). `continueList` answers null on a line that is not an item, so the
			// three presses that end a list are the editor's: one opens the next item, one leaves the
			// list because that item is empty, and the one after that sends.
			const inList = !inFence && start === end && continueList(value, start) !== null
			const sendKey =
				chord || (!noHoverPointer() && (settings.enterSends ? !e.shiftKey : e.shiftKey))
			if (sendKey && (!inFence || chord) && (!inList || chord)) {
				e.preventDefault()
				core.submit()
				return
			}
			if (!inFence && start === end) {
				const edit = continueList(value, start)
				if (edit !== null) {
					e.preventDefault()
					apply(edit)
				}
			}
			return
		}
		if (e.key === "Tab" && !e.ctrlKey && !e.metaKey && !e.altKey) {
			const edit = indentList(value, start, end, e.shiftKey)
			if (edit !== null) {
				e.preventDefault()
				apply(edit)
			}
			return
		}
		const shortcut = shortcutFor(e)
		if (shortcut !== null) {
			e.preventDefault()
			apply(applyShortcut(shortcut, value, start, end, placeholders))
			return
		}
		if (e.key === "ArrowUp" && value === "" && props.onEditLast !== undefined) {
			e.preventDefault()
			props.onEditLast()
			return
		}
		if (e.key === "Escape") {
			core.cancel?.()
			return
		}
		// Ctrl+Z in an empty box takes back a message still inside its undo window (spec 08 §4).
		// The outbox puts the text back into the draft and the core hears `undone` for the files;
		// nothing here has to touch the box.
		if ((e.ctrlKey || e.metaKey) && !e.shiftKey && e.code === "KeyZ" && value === "") {
			const outbox = currentOutbox()
			if (outbox === undefined) return
			e.preventDefault()
			void outbox.undoLatest(targetKey)
		}
	}

	/**
	 * A paste can carry files, text, or both. A screenshot is staged and the paste is over; a copy
	 * out of a document carries the picture *and* the words, and the words are inserted as well —
	 * a blanket `preventDefault()` on any paste that holds a file is how that text gets lost.
	 */
	const onPaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
		const clipboard = pastedContent(e.clipboardData)
		if (clipboard.files.length > 0) {
			attachments.attach(clipboard.files.map((file) => namePastedImage(file)))
			if (!clipboard.hasText) {
				e.preventDefault()
				return
			}
		}
		const pasted = pasteToMarkdown(e.clipboardData)
		if (pasted === null) return
		e.preventDefault()
		apply(pasteEdit(value, e.currentTarget.selectionStart, e.currentTarget.selectionEnd, pasted))
	}

	/**
	 * While this box is standing in for the editor, where the caret is *is* what the swap has to
	 * restore. It goes to the loader rather than into state: nothing on screen follows it, and a
	 * re-render per keystroke to remember a number would be one.
	 */
	const noteSwapCaret = (at: number) => {
		noteCaret?.({ caret: at, at: Date.now() })
	}

	const trackCaret = (e: React.SyntheticEvent<HTMLTextAreaElement>) => {
		setCaret(e.currentTarget.selectionStart)
		noteSwapCaret(e.currentTarget.selectionStart)
	}

	const loading = props.loading === true
	const showPreview = !loading && preview !== undefined && renderPreview !== undefined
	const label = translate(placeholderKey, placeholderArgs)

	return (
		<ComposerFrame
			core={core}
			belowTools={props.belowTools}
			preview={
				showPreview && previewOpen ? (
					<PreviewPane markdown={value} preview={preview} Render={renderPreview} />
				) : null
			}
			actions={
				loading ? null : (
					<>
						{showPreview ? (
							<Button
								quiet
								icon
								size="sm"
								className="tb-composer__tool"
								aria-pressed={previewOpen}
								aria-label={previewOpen ? t("composer.preview.hide") : t("composer.preview.show")}
								title={previewOpen ? t("composer.preview.hide") : t("composer.preview.show")}
								before={<Icon name="eye" />}
								onClick={() => setPreviewOpen((open) => !open)}
							/>
						) : null}
						{/* The way back to the formatted editor, drawn as the pressed half of the very
						    button the editor's toolbar ends with, so the two surfaces read as one
						    composer with a view rather than two products (X-A2 P9). */}
						<Button
							quiet
							icon
							size="sm"
							className="tb-composer__tool"
							aria-pressed
							aria-label={t("composer.menu.sourceMode")}
							title={t("composer.menu.sourceMode")}
							before={<Icon name="sourceCode" />}
							onClick={() => setSourceMode(false)}
						/>
					</>
				)
			}
		>
			{/* Both class names are the composer's input: `__input` is what every surface answers to
			    — the editor's contenteditable carries it too — and `__textarea` is this one's. The
			    `data-markdown` mirror is the hook tests and debugging read in both modes. */}
			<textarea
				ref={textareaRef}
				className="tb-composer__input tb-composer__textarea"
				rows={1}
				value={value}
				data-markdown={value}
				placeholder={label}
				aria-label={label}
				aria-describedby={core.describedBy}
				aria-invalid={core.tooLong || undefined}
				// The ARIA 1.2 combobox: the role and aria-expanded are what tell a screen
				// reader that a suggestion list appeared; aria-activedescendant alone announces
				// nothing (WCAG 4.1.2).
				role="combobox"
				aria-expanded={autocomplete.open}
				aria-autocomplete="list"
				aria-controls={autocomplete.open ? autocomplete.listboxId : undefined}
				aria-activedescendant={
					autocomplete.open ? autocomplete.optionId(autocomplete.activeIndex) : undefined
				}
				disabled={disabled}
				onChange={(e) => {
					setValue(e.target.value)
					setCaret(e.target.selectionStart)
					// Typing moves the caret, and the swap may come between this and the key's release:
					// a note that knows only where the caret was before the character would put the
					// editor's caret in front of what was just typed.
					noteSwapCaret(e.target.selectionStart)
					core.clearFailure()
				}}
				onKeyDown={onKeyDown}
				onKeyUp={trackCaret}
				onClick={trackCaret}
				onSelect={trackCaret}
				onPaste={onPaste}
				onFocus={trackCaret}
				onBlur={() => {
					setCaret(null)
					// Somebody leaving the box means the note no longer says where they were typing.
					// The swap is not this: React dispatches no blur for the element it removes, so
					// the note the editor restores survives its own stand-in.
					noteCaret?.(null)
					void draft.flush()
				}}
			/>
			<AutocompleteList state={autocomplete} />
		</ComposerFrame>
	)
}
